import { CONTAINER_TYPE_OPTIONS, type Incoterms, PortTypes } from 'api';
import {
  AddressAutocomplete,
  CurrencySelectField,
  IncotermSelectField,
  PortAutocomplete,
  SelectField,
  TradeCompanyAutocomplete,
  TradeCompanySiteSelector,
  useFormContext,
} from 'form';
import { useState } from 'react';
import { Grid, Typography } from 'ui';

import { useTransitTimeEstimator, useTransportEstimator } from '../../hooks';
import type { FormValues } from '../../pages/DealProfitabilityCalculator/DealProfitabilityCalculator';
import { areCoordinatesSetOnSite, isCollectionFromPort } from '../../utils';
import { TransportCostDetails } from '../TransportCostDetails/TransportCostDetails';

export const LogisticsSection = () => {
  const form = useFormContext<FormValues>();
  const [isAddressDisabled, setIsAddressDisabled] = useState(false);
  const { watch, setValue, setError } = form;
  const { estimateTransportPrice } = useTransportEstimator();
  const { estimateTransitTime } = useTransitTimeEstimator();
  const watchSeller = watch('seller') || undefined;
  const watchBuyer = watch('buyer') || undefined;

  const estimateTransportAndTransitTime = () => {
    estimateTransportPrice();
    estimateTransitTime();
  };
  return (
    <>
      <Typography variant="h5">Logistics</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" my={1}>
            Supply
          </Typography>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <TradeCompanyAutocomplete name="seller" label="Seller" />
            </Grid>
            <Grid item xs={12}>
              <TradeCompanySiteSelector
                name="seller_site"
                label="Seller site"
                tradeCompanyId={watchSeller}
                onSelectOption={(value) => {
                  if (!areCoordinatesSetOnSite(value)) {
                    setError('transport_price', { message: 'Invalid coordinates for seller site.' });
                    return;
                  }
                  setValue('origin_address', {
                    country_code: value.country,
                    full_address: value.full_address,
                    latitude: Number.parseFloat(value.latitude || '0'),
                    longitude: Number.parseFloat(value.longitude || '0'),
                  });
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <AddressAutocomplete
                name="origin_address"
                label="Origin address"
                onAddressSelected={estimateTransportAndTransitTime}
              />
            </Grid>
            <Grid item xs={12}>
              <PortAutocomplete
                name="port_of_loading"
                label="Port of loading"
                type={PortTypes.REG}
                onSelectOption={(event) => {
                  setValue('origin_port_country', event?.country);
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <IncotermSelectField
                name="supply_incoterm"
                label="Incoterm"
                required={false}
                fullWidth
                onChange={estimateTransportAndTransitTime}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectField
                label="Container Type"
                name="container_type"
                options={CONTAINER_TYPE_OPTIONS}
                onChange={estimateTransportAndTransitTime}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" my={1}>
            Demand
          </Typography>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <TradeCompanyAutocomplete name="buyer" label="Buyer" disabled={isAddressDisabled} />
            </Grid>
            <Grid item xs={12}>
              <TradeCompanySiteSelector
                name="buyer_site"
                label="Buyer site"
                tradeCompanyId={watchBuyer}
                disabled={isAddressDisabled}
                onSelectOption={(value) => {
                  if (!areCoordinatesSetOnSite(value)) {
                    setError('transport_price', { message: 'Invalid coordinates for buyer site.' });
                    return;
                  }
                  setValue('destination_address', {
                    country_code: value.country,
                    full_address: value.full_address,
                    latitude: Number.parseFloat(value.latitude || '0'),
                    longitude: Number.parseFloat(value.longitude || '0'),
                  });
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <AddressAutocomplete
                name="destination_address"
                label="Destination address"
                disabled={isAddressDisabled}
                onAddressSelected={estimateTransportAndTransitTime}
              />
            </Grid>
            <Grid item xs={12}>
              <PortAutocomplete
                name="port_of_discharge"
                label="Port of discharge"
                type={PortTypes.REG}
                onSelectOption={(event) => {
                  setValue('destination_port_country', event?.country);
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <IncotermSelectField
                name="demand_incoterm"
                label="Incoterm"
                required={false}
                fullWidth
                onChange={(event) => {
                  if (isCollectionFromPort(event.target.value as Incoterms)) {
                    setIsAddressDisabled(true);
                  } else {
                    setIsAddressDisabled(false);
                  }
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={12} md={6}>
            <CurrencySelectField
              label="Transport cost currency"
              name="transport_cost_currency"
              onChange={() => estimateTransportAndTransitTime()}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <TransportCostDetails />
        </Grid>
      </Grid>
    </>
  );
};
